import React from 'react'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faCloudArrowUp,
  faListCheck,
  faArrowRightArrowLeft,
} from '@fortawesome/pro-regular-svg-icons'
import { ButtonV2 } from '@labrav/react-components'
import { useStyles } from './CanvasMigrationPanel.styles'

const MIGRATION_STEPS = [
  {
    icon: faListCheck,
    title: 'Prepare',
    description:
      'We check your Salesforce connection and work out everything that needs to move.',
  },
  {
    icon: faArrowRightArrowLeft,
    title: 'Transfer',
    description:
      'Your referrals, offers, resale authorizations and configuration are copied across.',
  },
  {
    icon: faCircleCheck,
    title: 'Verify',
    description:
      'Every record is confirmed and linked so Canvas matches your existing data.',
  },
]

export type CanvasMigrationLandingViewProps = {
  onStart: () => void
  isStartDisabled: boolean
  isSubmitting: boolean
}

/**
 * Pre-start screen. Task cards are hidden here because the task list only
 * exists once the backend has created a job, so this explains the process
 * instead and carries the primary action.
 */
export const CanvasMigrationLandingView: React.FC<
  CanvasMigrationLandingViewProps
> = ({ onStart, isStartDisabled, isSubmitting }) => {
  const { classes } = useStyles()

  return (
    <div className={classes.landing} data-testid="canvas-migration-landing">
      <span className={classes.landingIcon}>
        <FontAwesomeIcon icon={faCloudArrowUp} />
      </span>

      <Typography className={classes.landingTitle} component="h2">
        Migrate your Salesforce data to Canvas
      </Typography>
      <Typography className={classes.landingDescription}>
        Moving to Canvas brings your referrals, private offers and resale
        authorizations into the new experience, with nothing left behind in your
        existing Salesforce org.
      </Typography>

      <div className={classes.landingSteps}>
        {MIGRATION_STEPS.map(step => (
          <div key={step.title} className={classes.landingStep}>
            <span className={classes.landingStepIcon}>
              <FontAwesomeIcon icon={step.icon} />
            </span>
            <Typography className={classes.landingStepTitle}>
              {step.title}
            </Typography>
            <Typography className={classes.landingStepText}>
              {step.description}
            </Typography>
          </div>
        ))}
      </div>

      <ButtonV2
        styleType="primary"
        disabled={isStartDisabled}
        loading={isSubmitting}
        onClick={onStart}
        data-testid="canvas-migration-start"
      >
        Start Migration
      </ButtonV2>

      <Typography className={classes.landingFootnote}>
        Progress for each step appears here once the migration begins. This
        usually takes a few minutes — keep this tab open while it runs.
      </Typography>
    </div>
  )
}
